import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  useTemplateForm,
  useTemplateProviders,
  useTemplateLoading,
  useTemplateSaving,
  useTemplateGeneration,
  useTemplateSync,
} from './useTemplateEditorHooks';

/**
 * Custom hook for managing TemplateEditor state and operations
 *
 * Provides:
 * - Form state management
 * - Template loading and saving
 * - AI template generation
 * - Provider and model management
 * - Error and success handling
 */
export const useTemplateEditor = () => {
  const navigate = useNavigate();
  const { templateId, versionId } = useParams();
  const location = useLocation();
  const backTarget = (location.state && location.state.from) || '/templates';

  // Form state
  const {
    formData,
    setFormData,
    newTag,
    setNewTag,
    handleInputChange,
    handleModelConfigChange,
    handleAddTag,
    handleRemoveTag,
    handleTagKeyPress,
  } = useTemplateForm();

  // Provider and model state
  const {
    providers,
    providerModels,
    loadingProviders,
    getModelsForProvider,
    getDefaultModelForProvider,
  } = useTemplateProviders();

  // Template loading
  const {
    loading,
    error: loadingError,
    setError: setLoadingError,
    loadedTemplateProvider,
    loadedTemplateModel,
    currentVersionId,
    currentVersionStatus,
    loadTemplate: loadTemplateBase,
  } = useTemplateLoading(versionId);

  // Store original template data to detect changes
  const [originalTemplateData, setOriginalTemplateData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Template saving
  const {
    saving,
    updatingMetadata,
    error: savingError,
    success,
    setError: setSavingError,
    setSuccess,
    handleSave: handleSaveBase,
    handleUpdateMetadata: handleUpdateMetadataBase,
    handleUpdateDraft: handleUpdateDraftBase,
  } = useTemplateSaving(templateId, backTarget, id => {
    loadTemplateBase(id, setFormData, setOriginalTemplateData);
  });

  // Template generation
  const {
    aiPrompt,
    generatingTemplate,
    setAiPrompt,
    handleGenerateTemplate: handleGenerateTemplateBase,
  } = useTemplateGeneration(
    setFormData,
    err => {
      setLoadingError(err);
      setSavingError(err);
    },
    setSuccess
  );

  // UI state
  const [showModelConfig, setShowModelConfig] = useState(true);

  // Sync template provider with adapters
  useTemplateSync(
    providers,
    loadedTemplateProvider,
    loadedTemplateModel,
    isEditMode,
    formData,
    setFormData,
    templateId
  );

  // Combined error state
  const error = loadingError || savingError;
  const setError = err => {
    setLoadingError(err);
    setSavingError(err);
  };

  // Load template wrapper
  const loadTemplate = useCallback(
    async id => {
      await loadTemplateBase(id, setFormData, setOriginalTemplateData);
    },
    [loadTemplateBase, setFormData]
  );

  // Load template if editing
  useEffect(() => {
    if (templateId && templateId !== 'new') {
      setIsEditMode(true);
      loadTemplate(templateId);
    } else {
      setIsEditMode(false);
    }
  }, [templateId, loadTemplate]);

  // Wrapper handlers
  const handleSave = useCallback(async () => {
    await handleSaveBase(
      formData,
      isEditMode,
      originalTemplateData,
      loadTemplate
    );
  }, [
    handleSaveBase,
    formData,
    isEditMode,
    originalTemplateData,
    loadTemplate,
  ]);

  const handleUpdateMetadata = useCallback(async () => {
    await handleUpdateMetadataBase(formData);
  }, [handleUpdateMetadataBase, formData]);

  const handleUpdateDraft = useCallback(async () => {
    await handleUpdateDraftBase(
      formData,
      currentVersionId,
      currentVersionStatus
    );
  }, [handleUpdateDraftBase, formData, currentVersionId, currentVersionStatus]);

  const handleGenerateTemplate = useCallback(async () => {
    await handleGenerateTemplateBase(formData);
  }, [handleGenerateTemplateBase, formData]);

  const handleCancel = useCallback(() => {
    navigate(backTarget);
  }, [navigate, backTarget]);

  // Helper functions are now provided by useTemplateProviders hook

  return {
    // State
    loading,
    saving,
    updatingMetadata,
    error,
    success,
    aiPrompt,
    generatingTemplate,
    formData,
    newTag,
    showModelConfig,
    providers,
    providerModels,
    loadingProviders,
    isEditMode,

    // Setters
    setAiPrompt,
    setNewTag,
    setShowModelConfig,
    setError,
    setSuccess,

    // Handlers
    handleInputChange,
    handleModelConfigChange,
    handleAddTag,
    handleRemoveTag,
    handleTagKeyPress,
    handleSave,
    handleUpdateMetadata,
    handleUpdateDraft,
    handleCancel,
    currentVersionStatus,
    handleGenerateTemplate,

    // Helper functions
    getModelsForProvider,
    getDefaultModelForProvider,
  };
};
